import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap } from 'lucide-react';
import { useOnboardingRedirect } from '../hooks/useOnboardingRedirect';
import { shouldSkipIntro } from '../utils/onboardingRouter';
import FlowGuard from '../../components/FlowGuard';

const BackgroundGrid = () => (
    <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden bg-[#F8F9FB]">
        {/* Ellipse 1: Main Gradient Background */}
        <div
            className="absolute"
            style={{
                width: '2618.47px',
                height: '1939.34px',
                left: '-448.14px',
                top: '-919.31px',
                background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.8) 0%, rgba(116, 153, 191, 0.465385) 41.83%, rgba(237, 242, 247, 0) 100%)',
                transform: 'rotate(26.84deg)',
                transformOrigin: 'top left',
            }}
        />

        {/* Ellipse 2827: Blurred Overlay */}
        <div
            className="absolute hidden md:block"
            style={{
                width: '640px',
                height: '458px',
                left: '50%',
                top: '282px',
                transform: 'translateX(-50%) matrix(1, 0, 0, -1, 0, 0)',
                background: 'rgba(255, 255, 255, 0.4)',
                filter: 'blur(93.2px)',
                borderRadius: '53px',
            }}
        />

        {/* Grid Lines */}
        <div className="absolute inset-0 flex justify-center pointer-events-none">
            <div className="w-full h-full max-w-[1920px] relative">
                {/* Vertical Lines */}
                {[...Array(12)].map((_, i) => (
                    <div
                        key={`v-${i}`}
                        className="absolute top-0 bottom-0 border-l border-dashed border-[rgba(40,40,42,0.2)]"
                        style={{ left: `${105 + (i * 120)}px` }}
                    />
                ))}

                {/* Horizontal Lines */}
                {[121, 300, 449, 627, 700, 872, 913, 1031].map((top, i) => (
                    <div
                        key={`h-${i}`}
                        className="absolute left-0 right-0 border-t border-dashed border-[rgba(40,40,42,0.2)]"
                        style={{ top: `${top}px` }}
                    />
                ))}
            </div>
        </div>
    </div>
);

const WelcomeIntroContent: React.FC = () => {
    const navigate = useNavigate();

    // Redirect users who have already completed onboarding
    useOnboardingRedirect();

    // Check if user should skip intro (has already uploaded/parsed resume)
    useEffect(() => {
        const checkIfShouldSkip = async () => {
            const userDataString = localStorage.getItem('authUser');
            if (userDataString) {
                try {
                    // Check if should skip intro using /me endpoint (JWT auth)
                    console.log('[WelcomeIntro] Checking if should skip intro using /me endpoint');
                    const skip = await shouldSkipIntro();

                    if (skip) {
                        console.log('[WelcomeIntro] User has existing data, skipping intro → resume-upload');
                        // User already has resume data, skip intro and go to resume upload page
                        // The resume upload page will handle further redirection if resume is already parsed
                        navigate('/welcome/resume-upload', { replace: true });
                    }
                } catch (error) {
                    console.error('[WelcomeIntro] Error checking skip status:', error);
                }
            }
        };

        checkIfShouldSkip();
    }, [navigate]);

    return (
        <div className="min-h-screen bg-[#F8F9FB] font-sans relative flex flex-col overflow-hidden">
            <BackgroundGrid />

            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center animate-fade-in relative z-10">
                <div className="mb-8 select-none">
                    <img
                        src="/branding/logo_blue.svg"
                        alt="Hush"
                        style={{
                            width: '100px',
                            height: '46px',
                            objectFit: 'contain'
                        }}
                    />
                </div>

                <div className="max-w-4xl mx-auto px-4">
                    <h1 className="text-[#015FA6] font-bold text-[36px] md:text-[48px] leading-[100%] mb-6 tracking-tight">
                        Welcome to Hush! <span className="inline-block animate-bounce">🎉</span>
                    </h1>
                    <p className="text-[#015FA6] font-extralight text-[20px] md:text-[24px] leading-[150%] mb-12">
                        We're thrilled to have you onboard. Let's set up your profile and find your next great opportunity together.
                    </p>

                    <button
                        onClick={() => navigate('/welcome/resume-upload')}
                        className="group relative inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#015FA6] text-white rounded-lg font-semibold text-lg hover:bg-[#014f8c] transition-all shadow-lg shadow-blue-900/20 hover:shadow-xl hover:-translate-y-0.5"
                    >
                        <Zap className="w-5 h-5 fill-current" />
                        Let's get started
                    </button>
                </div>
            </div>

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@200;300;400;500;600;700;800&display=swap');

                @keyframes fade-in {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                .animate-fade-in {
                    animation: fade-in 0.5s ease-out forwards;
                }
            `}</style>
        </div>
    );
};

/**
 * WelcomeIntro Page
 * Protected by FlowGuard - only accessible after candidate signup flow
 * Direct URL access redirects to login page
 */
const WelcomeIntro: React.FC = () => {
    return (
        <FlowGuard
            requiredStateKey="fromSignup"
            fallbackPath="/auth/login"
            alternateStateKeys={["fromCandidateSignup"]}
            allowIncompleteProfile={true}
        >
            <WelcomeIntroContent />
        </FlowGuard>
    );
};

export default WelcomeIntro;
